import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import {
  Blocker,
  Location,
  NavigationType,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { readHistoryIdx, searchHasSentinel } from './navigation-sentinel';

type NavDirection = 'back' | 'forward';

// PERMANENT instrumentation (TXCC-9643) — fires on every browser history POP (Back OR Forward, with
// a `direction` field) in both experiment buckets. NOT experiment-scoped: exclude from any future
// pp-backstop cleanup.
const BROWSER_NAV_EVENT = 'pp_browser_nav_button';

// PERMANENT instrumentation (TXCC-9911) — fires once per document teardown in both experiment
// buckets. NOT experiment-scoped: exclude from any future pp-backstop cleanup.
const APP_EXIT_EVENT = 'pp_app_exit';

// pp_browser_nav_button's established shape: absent when nothing is open, a bare string for one
// modal, an array for a stack. pp_app_exit reports the same registry, so it reports it the same way.
const toModalName = (names: string[]) =>
  names.length === 0 ? undefined : names.length === 1 ? names[0] : [...names];

interface NavigationAnalyticsContextValue {
  addOpenModal: (key: string) => void;
  removeOpenModal: (key: string) => void;
  // Reports a navigation a useBlocker held back. Returns the blocker with proceed() wrapped, so the
  // navigation it re-issues is not reported a second time.
  trackBlockedNavigation: (blocker: Blocker) => Blocker;
  // The next navigation away from the current location is programmatic, not the user's.
  ignoreNextNavigation: () => void;
}

const NavigationAnalyticsContext = createContext<NavigationAnalyticsContextValue>({
  addOpenModal: () => undefined,
  removeOpenModal: () => undefined,
  trackBlockedNavigation: blocker => blocker,
  ignoreNextNavigation: () => undefined
});

const NavigationAnalyticsContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { trackEvent } = useAnalyticsContext();

  // Layout effects run before any passive effect or cleanup in the same commit, so a child reading
  // these while its modal mounts or unmounts sees the location being committed.
  const locationRef = useRef(location);
  const trackEventRef = useRef(trackEvent);
  useLayoutEffect(() => {
    locationRef.current = location;
    trackEventRef.current = trackEvent;
  });

  // Modal open-presence registry — ordered by open sequence so the top-most (most recently opened)
  // modal is the last entry. Independent of back-close: a modal registers presence even when it
  // arms no guard.
  const openModalsRef = useRef<string[]>([]);

  // Presence as of the moment the user pressed Back/Forward. A POP that actually navigates unmounts
  // the open modal in the SAME commit as the location change, and React runs every effect cleanup
  // before any effect — so by the time the navigation effect below runs, the registry describes the
  // page arrived at, not the page left (TXCC-9808). locationRef is already the new location when
  // those cleanups and effects run, so the first registry change made while it differs from the
  // location that effect last reported belongs to that commit: snapshot what was open just before.
  const reportedLocationRef = useRef(location);
  const modalsAtNavigationRef = useRef<string[] | null>(null);
  const beforeRegistryChange = useCallback(() => {
    if (modalsAtNavigationRef.current !== null) return;
    if (locationRef.current === reportedLocationRef.current) return;
    modalsAtNavigationRef.current = [...openModalsRef.current];
  }, []);

  const addOpenModal = useCallback(
    (key: string) => {
      if (openModalsRef.current.includes(key)) return;
      beforeRegistryChange();
      openModalsRef.current.push(key);
    },
    [beforeRegistryChange]
  );

  const removeOpenModal = useCallback(
    (key: string) => {
      if (!openModalsRef.current.includes(key)) return;
      beforeRegistryChange();
      openModalsRef.current = openModalsRef.current.filter(k => k !== key);
    },
    [beforeRegistryChange]
  );

  const emitBrowserNav = useCallback(
    (from: string, to: string, block: boolean, direction: NavDirection, names: string[]) => {
      trackEventRef.current(BROWSER_NAV_EVENT, {
        from,
        to,
        modalOpen: names.length > 0,
        modalName: toModalName(names),
        block,
        direction
      });
    },
    []
  );

  // Holds the location current when a caller announced a programmatic navigation. Resolving
  // "the next navigation" against it, rather than setting a plain flag, keeps a caller running
  // inside a navigation's own commit — the coordinator's child effects run before this provider's —
  // from swallowing the report for the navigation that is committing.
  const ignoreAfterRef = useRef<Location | null>(null);
  const ignoreNextNavigation = useCallback(() => {
    ignoreAfterRef.current = locationRef.current;
  }, []);

  const prevIdxRef = useRef<number>(readHistoryIdx() ?? 0);

  // Called from the blocker's effect, which for a POP runs in the microtask React flushes the
  // popstate's discrete update in (react-dom 18.3) — before the async history.go() react-router
  // issues to restore the URL. The live index is therefore still the POP's target.
  const trackBlockedNavigation = useCallback(
    (blocker: Blocker): Blocker => {
      const here = locationRef.current.pathname;
      const target = readHistoryIdx();
      const direction: NavDirection =
        target !== undefined && target > prevIdxRef.current ? 'forward' : 'back';
      // A blocked navigation never commits, so nothing unmounted and the live registry is accurate.
      emitBrowserNav(
        here,
        blocker.location?.pathname ?? here,
        true,
        direction,
        openModalsRef.current
      );
      return {
        ...blocker,
        proceed:
          blocker.proceed &&
          (() => {
            ignoreNextNavigation();
            blocker.proceed?.();
          })
      } as Blocker;
    },
    [emitBrowserNav, ignoreNextNavigation]
  );

  const prevLocationRef = useRef(location);
  useEffect(() => {
    const prev = prevLocationRef.current;
    prevLocationRef.current = location;
    const prevIdx = prevIdxRef.current;
    const idx = readHistoryIdx();
    if (idx !== undefined) prevIdxRef.current = idx;
    // react-router hands back the same location object across re-renders, so this is also what
    // skips the initial mount and collapses a repeat run into the one report its navigation gets.
    if (prev === location) return;

    // Consumed before the non-reporting returns below so it cannot leak into a later navigation.
    const modalsAtNavigation = modalsAtNavigationRef.current ?? [...openModalsRef.current];
    modalsAtNavigationRef.current = null;
    reportedLocationRef.current = location;

    if (ignoreAfterRef.current !== null && ignoreAfterRef.current !== location) {
      ignoreAfterRef.current = null;
      return;
    }
    if (navigationType !== NavigationType.Pop) return;

    const direction: NavDirection = idx !== undefined && idx > prevIdx ? 'forward' : 'back';
    // Leaving a sentinel entry with a modal open IS the back-close: the POP closed it rather than
    // navigating the user anywhere, which is what `block` reports. A sentinel stranded in history
    // makes the same URL transition with nothing open (TXCC-9811), and a Forward off one makes it
    // while the user does travel — neither is a block.
    const closedAModal =
      direction === 'back' &&
      modalsAtNavigation.length > 0 &&
      searchHasSentinel(prev.search) &&
      !searchHasSentinel(location.search);
    emitBrowserNav(prev.pathname, location.pathname, closedAModal, direction, modalsAtNavigation);
  }, [location, navigationType, emitBrowserNav]);

  // The only signal that survives Back leaving the document: a Back from the router's root entry is
  // a cross-document navigation, so popstate never fires, react-router never commits, and neither
  // pp_browser_nav_button nor EventedModal's pp_modal_closed is ever generated — not even its
  // unmount teardown (TXCC-9867), which needs a live React tree.
  const exitEmittedRef = useRef(false);
  useEffect(() => {
    const emitExit = (reason: 'pagehide' | 'hidden', persisted: boolean) => {
      if (exitEmittedRef.current) return;
      exitEmittedRef.current = true;
      const names = openModalsRef.current;
      trackEventRef.current(APP_EXIT_EVENT, {
        reason,
        persisted,
        modalOpen: names.length > 0,
        modalName: toModalName(names)
      });
    };

    // pagehide is the primary hook: the documented terminal signal, it fires where beforeunload is
    // unreliable or absent (mobile Safari), and it reports bfcache via `persisted`. Delivery from here
    // is sound — both sendBeacon and dev-mode keepalive fetch were measured leaving this handler,
    // including behind the awaits the clickstream envelope performs and behind a delayed CORS
    // preflight. It is deliberately NOT beforeunload, which is legacy for this purpose and would be
    // chosen only because it is easier to watch in devtools.
    const onPageHide = (event: PageTransitionEvent) => {
      emitExit('pagehide', event.persisted);
    };

    // Covers what pagehide cannot: a backgrounded tab the user may never return from, and iOS putting
    // a document away without a pagehide. Scoped to an open modal because it otherwise fires on every
    // tab switch, which is not an exit; `reason` separates the two downstream. Becoming visible
    // re-arms, so a later real exit still reports.
    //
    // On a real exit this ALSO fires, ~5ms after pagehide (measured), so the emit guard is what keeps
    // one exit to one event rather than a nicety — do not remove it.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') {
        exitEmittedRef.current = false;
        return;
      }
      if (openModalsRef.current.length > 0) emitExit('hidden', false);
    };

    // A bfcache restore reuses this document rather than building a new one, so the guard has to
    // re-arm here too or the eventual real exit is swallowed.
    const onPageShow = () => {
      exitEmittedRef.current = false;
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <NavigationAnalyticsContext.Provider
      value={{ addOpenModal, removeOpenModal, trackBlockedNavigation, ignoreNextNavigation }}
    >
      {children}
    </NavigationAnalyticsContext.Provider>
  );
};

const useNavigationAnalyticsContext = () => useContext(NavigationAnalyticsContext);

export { NavigationAnalyticsContextProvider, useNavigationAnalyticsContext };
export type { NavigationAnalyticsContextValue };
